import * as React from 'react';
import { useContext } from 'react';
import { produce } from 'immer';
import { currentUrl, replaceState, URLX } from '@avensia/excite';
import { SEARCH_NOTIFY_QUERY_NAME, SEARCH_PARAMS_COUNT, SEARCH_PARAMS_PAGE } from '@avensia/excite-esales';

export const SeoSubpageContext = React.createContext<
  | {
      facets: Excite.Esales.FacetApiModel[];
      subPageData: Required<NordicNest.Api.ProductListingPageApiModel>['subPageData'];
    }
  | undefined
>(undefined);

export function SeoSubpageProvider({
  facets,
  subPageData,
  children,
}: React.PropsWithChildren<{
  facets: Excite.Esales.FacetApiModel[];
  subPageData: NordicNest.Api.ProductListingPageApiModel['subPageData'];
}>) {
  if (!subPageData) {
    return <>{children}</>;
  }
  return <SeoSubpageContext.Provider value={{ facets, subPageData }}>{children}</SeoSubpageContext.Provider>;
}

function clearFacets(facets: Excite.Esales.FacetApiModel[]) {
  const url = currentUrl();

  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  url.searchParams.delete(SEARCH_PARAMS_COUNT);

  for (const facet of facets) {
    url.searchParams.delete(facet.attribute);
  }
  return replaceState(url, { shouldUseCache: false });
}

function getFacetUrl() {
  const url = currentUrl();
  url.hiddenParams.set(SEARCH_NOTIFY_QUERY_NAME, 'true');
  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  url.searchParams.delete(SEARCH_PARAMS_COUNT);
  return url;
}

function allSelectedValues(
  values: Excite.Esales.FacetValueApiModel[],
  selectedValues: Excite.Esales.FacetValueApiModel[] = [],
) {
  selectedValues.push(...values.filter(v => v.selected));
  return selectedValues;
}

function updateUrlWithFacetSearchParams(facets: Excite.Esales.FacetApiModel[]) {
  const url = getFacetUrl();

  for (const facet of facets) {
    if (!facet.isRange) {
      url.searchParams.delete(facet.attribute);
      for (const selected of allSelectedValues(facet.values)) {
        url.searchParams.append(facet.attribute, selected.text);
      }
    }
  }
  return replaceState(url, { shouldUseCache: false });
}

function useFacetsSelection(
  facets: Excite.Esales.FacetApiModel[],
  config?: { onUrlUpdate?: (facets: Excite.Esales.FacetApiModel[]) => unknown },
): [Excite.Esales.FacetApiModel[], (facet: Excite.Esales.FacetApiModel) => Promise<void>, () => void] {
  const lastFacetsRef = React.useRef(facets);
  const [facetsState, setFacets] = React.useState(facets);
  const configuredOnUrlUpdate = config?.onUrlUpdate;

  React.useEffect(() => {
    if (lastFacetsRef.current !== facets) {
      setFacets(facets);
      lastFacetsRef.current = facets;
    }
  }, [facets]);

  const clearFacetValues = React.useCallback(() => {
    const newFacets = facetsState.map(facet => {
      if (facet.isRange) {
        return {
          ...facet,
          range: {
            max: facet.range.max,
            maxSelected: facet.range.max,
            minSelected: facet.range.min,
            min: facet.range.min,
          },
        };
      } else {
        const values = facet.values.map(value => ({ ...value, selected: false }));
        return { ...facet, values };
      }
    });
    setFacets(newFacets);
    configuredOnUrlUpdate ? configuredOnUrlUpdate(newFacets) : clearFacets(newFacets);
  }, [configuredOnUrlUpdate, facetsState]);

  const onFacetValueChange = React.useCallback(
    async (facet: Excite.Esales.FacetApiModel) => {
      const index = facetsState.findIndex(f => f.attribute === facet.attribute);
      const newFacetsState = [...facetsState];
      newFacetsState[index] = { ...facet };
      setFacets(newFacetsState);
      if (configuredOnUrlUpdate) {
        await configuredOnUrlUpdate(newFacetsState);
      } else {
        updateUrlWithFacetSearchParams(newFacetsState);
      }
    },
    [facetsState, configuredOnUrlUpdate],
  );

  return [facetsState, onFacetValueChange, clearFacetValues];
}

export function useSeoSubpageData() {
  const data = useContext(SeoSubpageContext);

  return data;
}

export function getSeoSubpageUrl(
  subpageData: Required<NordicNest.Api.ProductListingPageApiModel>['subPageData'],
  facets: Excite.Esales.FacetApiModel[],
) {
  const url = currentUrl();
  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  let facetsInUrl = facets;
  let isSubpage = false;
  if (subpageData) {
    const selectedFacets = findSelectedSeoSubPageFacets(subpageData, facets);
    updateUrlIfMatchSeoSubPageFacets(selectedFacets);
    facetsInUrl = updateSelectedFacetsBaseSeoSubPageData(selectedFacets, facetsInUrl, url);
  }

  addAdditionalFacetsToUrl(facetsInUrl, url);

  return [url, isSubpage] as [URLX, boolean];

  function updateUrlIfMatchSeoSubPageFacets(selectedFacets: [string, NordicNest.Api.ChosenFacet[]] | undefined) {
    const slugs = url.pathname.split('/');
    const filteredSlugs = slugs.filter(Boolean);
    const areWeOnSubpage = filteredSlugs.findIndex(s => s === subpageData.root) !== filteredSlugs.length - 1;
    if (selectedFacets) {
      isSubpage = true;
      if (areWeOnSubpage) {
        slugs[slugs.length - 2] = selectedFacets[0];
      } else {
        slugs.splice(slugs.length - 1, 0, selectedFacets[0]);
      }
    } else if (areWeOnSubpage) {
      slugs.splice(slugs.length - 2, 1);
    }
    url.pathname = slugs.join('/');
  }
}

function addAdditionalFacetsToUrl(facetsInUrl: Excite.Esales.FacetApiModel[], url: URLX) {
  for (const facet of facetsInUrl) {
    url.searchParams.delete(facet.attribute);
    if (!facet.isRange) {
      for (const selected of facet.values.filter(e => e.selected)) {
        url.searchParams.append(facet.attribute, selected.text);
      }
    } else if (facet.range.maxSelected !== facet.range.max || facet.range.minSelected !== facet.range.min) {
      url.searchParams.append(facet.attribute, `[${facet.range.minSelected},${facet.range.maxSelected}]`);
    }
  }
}

function updateSelectedFacetsBaseSeoSubPageData(
  selectedFacets: [string, NordicNest.Api.ChosenFacet[]] | undefined,
  facetsInUrl: Excite.Esales.FacetApiModel[],
  url: URLX,
) {
  if (selectedFacets) {
    facetsInUrl = produce(facetsInUrl, draftFacets => {
      for (const chosenFacet of selectedFacets[1]) {
        const facetIndex = draftFacets.findIndex(e => e.attribute === chosenFacet.key);
        if (facetIndex === -1) {
          throw new Error('unable to find facet for ' + chosenFacet.key);
        }
        const facet = draftFacets[facetIndex];
        if (facet.isRange && chosenFacet.value.range) {
          if (
            facet.range.minSelected === chosenFacet.value.range.min &&
            facet.range.maxSelected === chosenFacet.value.range.max
          ) {
            draftFacets.splice(facetIndex, 1);
            url.searchParams.delete(facet.attribute);
          }
        } else if (!facet.isRange && chosenFacet.value.single) {
          const singleValue = chosenFacet.value.single;
          const value = facet.values.find(v => v.text === singleValue.value);
          if (!value) {
            throw new Error(`unable to find facet value ${chosenFacet.value} for facet ${chosenFacet.key}`);
          }
          value.selected = false;
        }
      }
    });
  }
  return facetsInUrl;
}

function findSelectedSeoSubPageFacets(
  subpageData: NordicNest.Api.SubpageRoutes,
  facets: Excite.Esales.FacetApiModel[],
) {
  return Object.entries(subpageData.subPages).find(([, chosenFacets]) => {
    const collection = chosenFacets.reduce<{ [k: string]: NordicNest.Api.FacetValue[] }>(function (acc, item) {
      (acc[item.key] = acc[item.key] || []).push(item.value);
      return acc;
    }, {});

    return Object.entries(collection).every(([key, facetValue]) => {
      const facet = facets.find(f => f.attribute === key);
      if (!facet) {
        return false;
      }
      if (facet?.isRange && facetValue.length > 1) {
        return false;
      }
      if (facet?.isRange && facetValue[0].range) {
        if (facet.range.min === facet.range.minSelected && facet.range.maxSelected === facet.range.max) {
          return false;
        }
        if (typeof facetValue[0].range.min !== 'undefined' && facet.range.minSelected < facetValue[0].range.min)
          return false;
        if (typeof facetValue[0].range.max !== 'undefined' && facet.range.maxSelected > facetValue[0].range.max)
          return false;
        return true;
      } else if (!facet?.isRange && facetValue[0].single) {
        const totalSelected = facet?.values?.reduce((acc, i) => (i.selected ? acc + 1 : acc), 0);
        if (totalSelected !== facetValue.length && facetValue[0].single.value !== 'true') {
          return false;
        }
        const allMatched = facetValue.every(i => facet?.values?.find(v => v.selected && v.text === i.single?.value));
        return allMatched;
      }
    });
  });
}

export function useFacetValues(facet: Excite.Esales.FacetApiModel) {
  const seoSubpageData = useSeoSubpageData();
  const values = React.useMemo<[Excite.Esales.FacetValueApiModel, URLX | undefined][]>(() => {
    if (facet.isRange) {
      return facet.values.map(v => [v, undefined]);
    }
    return facet.values.map(fv => {
      if (!seoSubpageData) return [fv, undefined];
      const newFacets = produce(seoSubpageData.facets, fs => {
        const f = fs.find(f => f.attribute === facet.attribute);
        const value = f?.values.find(v => v.text === fv.text);
        if (!value) {
          return;
        }

        value.selected = !value.selected;
      });
      const [url, toSubpage] = getSeoSubpageUrl(seoSubpageData.subPageData, newFacets);
      if (!url || !toSubpage || url.search) {
        return [fv, undefined];
      } else {
        return [fv, url];
      }
    });
  }, [facet.isRange, facet.values, facet.attribute, seoSubpageData]);
  return values;
}

export function useSubPageFacetsSelection(
  facets: Excite.Esales.FacetApiModel[],
  subPageData: NordicNest.Api.ProductListingPageApiModel['subPageData'],
): ReturnType<typeof useFacetsSelection> {
  const onUrlUpdate = React.useCallback(
    (facets: Excite.Esales.FacetApiModel[]) => {
      // onUrlUpdate is not used if subPageData is falsy
      const [url] = getSeoSubpageUrl(subPageData!, facets);
      replaceState(url, { shouldUseCache: false, keepComponents: true });
    },
    [subPageData],
  );
  return useFacetsSelection(facets, subPageData ? { onUrlUpdate } : undefined);
}
